/**
 * Browser image search (ticket 09): a free-text query is embedded by UForm's
 * text encoder and cosine-ranked against the server-produced image embeddings
 * already mirrored to the client. This module is the *facade* — the pure
 * candidate collection and ranking live in `./imageRanking` (no heavy dep), and
 * this file hosts the WASM model runner {@link UformImageSearchService}.
 */

import { UformTextEncoder } from "@egalregal/uform";
import { log } from "../../logger";
import { getModelBytes, putModelBytes } from "../../shared/lib/browserModelStore";
import { UFORM_MODEL_DIR, UFORM_MODEL_VERSION } from "../../shared/lib/modelManifest";

export * from "./imageRanking";

/** URL path (relative to the SPA origin) of the vendored text-encoder files. */
export const IMAGE_SEARCH_MODEL_DIR = UFORM_MODEL_DIR;

/** Download progress as a 0..1 fraction. */
export type ProgressListener = (fraction: number) => void;

/**
 * Lazy UForm text-encoder runner for the browser (ticket 09). Serves the
 * vendored `tokenizer.json` + `text_encoder.onnx` from the Browser model
 * store (IndexedDB) when warm — with no network fetch and no progress events,
 * so the gallery shows its loading indicator — and downloads them same-origin
 * on a cold miss, with byte progress reported through {@link onProgress},
 * then persists them for the next visit. Stale entries (version/size
 * mismatch) are refetched and replaced. A failed load resolves `false` via
 * `ready` (mirroring the EmbeddingGemma service) and the gallery shows its
 * offline/error state instead of crashing.
 */
export class UformImageSearchService {
  #encoderPromise: Promise<UformTextEncoder | null> | null = null;
  #readyPromise: Promise<boolean> | null = null;
  #onProgress?: ProgressListener;

  constructor(private readonly modelDir = IMAGE_SEARCH_MODEL_DIR) {}

  /** Register (or clear) the model-download progress listener. */
  onProgress(listener: ProgressListener | undefined): void {
    this.#onProgress = listener;
  }

  /** Resolves true once the encoder is loaded; a failed load retries next time. */
  get ready(): Promise<boolean> {
    if (!this.#readyPromise) {
      this.#readyPromise = this.#load().then((encoder) => {
        if (!encoder) {
          this.#readyPromise = null;
          this.#encoderPromise = null;
        }
        return encoder !== null;
      });
    }
    return this.#readyPromise;
  }

  /** Embed text into a raw 256-dim vector; throws if the encoder is unavailable. */
  async embed(text: string): Promise<number[]> {
    const encoder = await this.#load();
    if (!encoder) throw new Error("UForm text encoder is unavailable");
    return encoder.encode(text);
  }

  #load(): Promise<UformTextEncoder | null> {
    if (!this.#encoderPromise) {
      this.#encoderPromise = this.#create().catch((err) => {
        this.#encoderPromise = null;
        log.warn({ err }, "[image-search] UForm text encoder failed to load");
        return null;
      });
    }
    return this.#encoderPromise;
  }

  async #create(): Promise<UformTextEncoder> {
    const urls = [`${this.modelDir}/tokenizer.json`, `${this.modelDir}/text_encoder.onnx`];
    // Warm path first: a store hit for both files means no network fetch and
    // no progress events (the gallery keeps its loading indicator).
    const cached = await Promise.all(
      urls.map((url) => getModelBytes(url, UFORM_MODEL_VERSION)),
    );
    let tokenizerBytes = cached[0];
    let modelBytes = cached[1];
    if (!tokenizerBytes || !modelBytes) {
      const missing = urls.filter((_, i) => !cached[i]);
      const fetched = await fetchModelFiles(missing, (fraction) =>
        this.#onProgress?.(fraction),
      );
      await Promise.all(
        missing.map((url, i) => putModelBytes(url, UFORM_MODEL_VERSION, fetched[i]!)),
      );
      const byUrl = new Map(missing.map((url, i) => [url, fetched[i]!] as const));
      tokenizerBytes ??= byUrl.get(urls[0]!)!;
      modelBytes ??= byUrl.get(urls[1]!)!;
    }
    const tokenizerJson = JSON.parse(new TextDecoder().decode(tokenizerBytes!)) as unknown;
    return UformTextEncoder.create({ model: modelBytes!, tokenizerJson });
  }
}

/** Shared lazy singleton used by the gallery's appearance mode. */
export const imageSearchService = new UformImageSearchService();

/**
 * Fetch a set of model files with aggregate byte progress. Progress is the
 * running `loaded / total` across all files, where `total` grows as each file's
 * `content-length` becomes known — a temporary re-scale when a later (larger)
 * file starts is expected and harmless for a progress bar.
 */
async function fetchModelFiles(
  urls: readonly string[],
  onProgress: (fraction: number) => void,
): Promise<Uint8Array[]> {
  let loaded = 0;
  let total = 0;
  const out: Uint8Array[] = [];
  for (const url of urls) {
    let fileLoaded = 0;
    let fileTotal = 0;
    out.push(
      await fetchWithProgress(url, (l, t) => {
        loaded += l - fileLoaded;
        fileLoaded = l;
        if (t > 0 && t !== fileTotal) {
          total += t - fileTotal;
          fileTotal = t;
        }
        onProgress(total > 0 ? Math.min(1, loaded / total) : 0);
      }),
    );
  }
  return out;
}

/** Stream a URL and report `(loadedBytes, totalBytes)` as it arrives. */
async function fetchWithProgress(
  url: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok || !response.body) {
    throw new Error(`Failed to fetch ${url}: HTTP ${response.status}`);
  }
  const total = Number(response.headers.get("content-length")) || 0;
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress(loaded, total);
  }
  const merged = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return merged;
}
